import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Card, CardBody, Col } from "react-bootstrap";
import { usePagination } from "../../shared/hooks/usePagination";
import DeleteModal from "../../Common/DeleteModal";
import { CustomPagination } from "../../shared/components/CustomPagination";
import { FlatMaterialService } from "./service";
import { IMaterial } from "../../shared/interfaces/material/IMaterial";
import { formatGermanNumber } from "../../shared/utils/functions";

const PER_PAGE_DEFAULT = 20;

const FlatMaterialList = () => {
  const { t } = useTranslation();
  const [list, setList] = useState<IMaterial[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<number | null>(null);
  const {
    page,
    handlePages,
    pagination,
    totalPages,
    setTotalPages,
    goToPreviousPage,
    goToNextPage,
  } = usePagination({ perPage: PER_PAGE_DEFAULT });

  const fetchData = () => {
    FlatMaterialService.getAll(pagination)
      .then((response) => {
        setList(response.data ?? []);
        setTotalPages(response.totalPages);
      })
      .catch(ErrorToast);
  };

  useEffect(() => {
    fetchData();
  }, [pagination]);

  const handleMakeReal = (id: number) => {
    FlatMaterialService.updateStatus(id, false)
      .then(() => {
        SuccessToast(t("flatMaterial.madeRealSuccess"));
        fetchData();
      })
      .catch(ErrorToast);
  };

  const handleDeleteClick = (id: number) => {
    setMaterialToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (materialToDelete !== null) {
      FlatMaterialService.delete(materialToDelete)
        .then(() => {
          SuccessToast(t("flatMaterial.deleteSuccess"));
          setShowDeleteModal(false);
          setMaterialToDelete(null);
          fetchData();
        })
        .catch(ErrorToast);
    }
  };

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>{t("material.id")}</th>
                    <th>{t("material.name")}</th>
                    <th>{t("flatMaterial.flat")}</th>
                    <th>{t("material.materialType")}</th>
                    <th>{t("material.supplier")}</th>
                    <th>{t("material.costPrice")}</th>
                    <th>{t("material.updatedAt")}</th>
                    <th className="text-end">{t("material.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item: any) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {item.materialImage?.path && (
                            <img
                              src={item.materialImage.path}
                              alt={item.name}
                              style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4 }}
                            />
                          )}
                          {item.name}
                        </div>
                      </td>
                      <td>{item.flat?.name ?? "-"}</td>
                      <td>{item.materialType?.name ?? "-"}</td>
                      <td>{item.supplier?.name ?? "-"}</td>
                      <td>{formatGermanNumber(item.costPrice)}</td>
                      <td>
                        {item.updatedAt
                          ? new Date(item.updatedAt).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="text-end">
                        <ul className="list-inline mb-0">
                          <li className="list-inline-item">
                            <button
                              className="btn btn-sm btn-success"
                              title={t("flatMaterial.makeReal")}
                              onClick={() => handleMakeReal(item.id)}
                            >
                              <i className="ti ti-check f-16 me-1"></i>
                              {t("flatMaterial.makeReal")}
                            </button>
                          </li>
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-danger btn-pc-default"
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                  {list.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        {t("general.loading")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <CustomPagination
              page={page}
              totalPages={totalPages}
              handlePages={handlePages}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
            />
          </CardBody>
        </Card>
      </Col>
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={t("flatMaterial.material")}
      />
    </React.Fragment>
  );
};

export default FlatMaterialList;
